import React from 'react';
import ReactDOM from "react-dom";
import App from "./App";
import ApolloClient from 'apollo-boost';
import { ApolloProvider } from '@apollo/react-hooks';
import config from "./config";
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { BrowserRouter as Router } from 'react-router-dom';
import awsmobile from './aws-exports';
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";

if (process.env.REACT_APP_SENTRY) {
  Sentry.init({
    dsn: "https://4ed1a6ff40b94a18ba72a54a7180a44d@o406326.ingest.sentry.io/6463377",
    integrations: [new BrowserTracing(), new Sentry.Replay()],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const apolloUri = Boolean(awsmobile.aws_cloud_logic_custom) ? 
  `${awsmobile.aws_cloud_logic_custom[0].endpoint}/graphql` : 
  process.env.REACT_APP_APOLLO_CLIENT;

const ApolloProviderWithAuth = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const client = new ApolloClient({
    uri: apolloUri,
    request: async (operation) => {
      if (!isAuthenticated) {
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        if (!token) {
          return;
        }

        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            Authorization: `Bearer ${token}`
          }
        }));
      } catch (error) {
        console.error('Failed to attach Auth0 access token to GraphQL request:', error);
      }
    }
  });

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

// Add error boundary component
const Auth0ProviderWithErrorBoundary = ({ children }) => {
  const onRedirectCallback = (appState) => {
    try {
      // Store the state before redirect
      const currentState = sessionStorage.getItem('auth_state');
      console.log('Stored state:', currentState);
      console.log('Received state:', appState?.state);

      // Clean up storage
      sessionStorage.removeItem('auth_state');

      // Clean up any existing tokens
      localStorage.removeItem('auth0.is.authenticated');
      localStorage.removeItem('auth0.token');

      // Remove auth parameters from URL
      window.history.replaceState(
        {},
        document.title,
        appState?.returnTo || window.location.pathname
      );
    } catch (error) {
      console.error('Error in redirect callback:', error);
    }
  };

  const onError = (error) => {
    console.error('Auth0 Error:', error);
    // If there's a token error, clear storage and reload
    if (error.message?.includes('refresh token') || error.message?.includes('403')) {
      console.log('Clearing auth state due to token error');
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = window.location.origin;
    }
  };

  return (
    <Auth0Provider
      domain={config.auth0.domain}
      clientId={config.auth0.clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: config.auth0.audience,
        scope: config.auth0.scope
      }}
      onRedirectCallback={onRedirectCallback}
      onError={onError}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={false}
    >
      {children}
    </Auth0Provider>
  );
};

ReactDOM.render(
  <Auth0ProviderWithErrorBoundary>
    <Router basename={config.app.basename}>
      <ApolloProviderWithAuth>
        <App />
      </ApolloProviderWithAuth>
    </Router>
  </Auth0ProviderWithErrorBoundary>,
  document.getElementById("root")
);
